import styled from 'styled-components';

/* Styles for the referral/promo field that sits under the offer summary, next to
   the discount it produces. Ported from the site's contract-flow step-select
   design; the page chrome (pane/card/eyebrow/footer) comes from ReviewOffer. */

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

export const Label = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 1.4rem;
  font-weight: 600;
  color: #e5e5e5;

  .req {
    color: #ff9a9a;
  }
`;

export const Control = styled.div`
  margin-top: 12px;
`;

export const ReferralRow = styled.div`
  display: flex;
  gap: 8px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

export const ReferralInput = styled.input<{ $applied?: boolean }>`
  flex: 1;
  min-width: 0;
  height: 44px;
  border-radius: 8px;
  border: 1.5px solid rgba(255, 255, 255, 0.14);
  background: #2b2a3a;
  padding: 0 16px;
  font-family: 'Inter', sans-serif;
  font-size: 1.4rem;
  /* Green once the code is bound, so the field itself carries the confirmation.
     Lifted for the dark card — #1f7a44 is unreadable on #2b2a3a. */
  color: ${({ $applied }) => ($applied ? '#7ee2a8' : '#ffffff')};
  font-weight: ${({ $applied }) => ($applied ? 600 : 400)};
  text-transform: uppercase;

  &::placeholder {
    color: rgba(255, 255, 255, 0.45);
    text-transform: none;
  }

  &:focus {
    outline: none;
    border-color: #5968db;
    box-shadow: 0 0 0 3px rgba(89, 104, 219, 0.4);
  }
`;

/* Outlined rather than filled: Apply is secondary here — the screen's primary
   action is the footer CTA, and two solid buttons would compete. */
export const ApplyButton = styled.button`
  height: 44px;
  border-radius: 8px;
  border: 1.5px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.06);
  color: #ffffff;
  padding: 0 24px;
  font-family: 'Inter', sans-serif;
  font-size: 1.4rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: rgba(255, 255, 255, 0.28);
    color: #ffffff;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

/* Applied state as a note under the still-editable input, rather than replacing it.
   The code stays changeable: re-applying a different one rebinds it on the backend
   (set_referral reassigns contract_flow.member_referral). */
/* The rate the applied code earns. inline-flex so the badge is only as wide as its
   own text — stretched across the row it read as a banner rather than a tag on the
   field above. The code itself is not repeated here; it is already in the input. */
export const AppliedNote = styled.p`
  display: inline-flex;
  align-items: center;
  margin: 8px 0 0;
  border-radius: 8px;
  border: 1px solid rgba(126, 226, 168, 0.35);
  background: rgba(126, 226, 168, 0.1);
  padding: 8px 12px;
  font-size: 1.4rem;
  color: #7ee2a8;
`;

/* The rejected code, given the same badge as the applied one in the opposite accent:
   same geometry, red instead of green. As a bare line of text it read as a caption
   under the field rather than the answer to the Apply the member just pressed.
   inline-flex for the same reason as AppliedNote — only as wide as its own text. */
export const ReferralError = styled.p`
  display: inline-flex;
  align-items: center;
  margin: 8px 0 0;
  border-radius: 8px;
  border: 1px solid rgba(255, 154, 154, 0.35);
  background: rgba(255, 154, 154, 0.1);
  padding: 8px 12px;
  font-size: 1.4rem;
  font-weight: 500;
  color: #ff9a9a;
`;
